// Section « Journal d'activité » de la fiche appareil.
//
// Le journal répond à des questions que ni les alertes ni l'inventaire ne
// savent traiter : « la machine était-elle en ligne ce soir-là », « qui a lancé
// ces mises à jour », « qui était devant le poste ». Une alerte décrit un ÉTAT
// qui dure et finit par se résoudre ; ici ce sont des ÉVÉNEMENTS ponctuels, qui
// ne se résolvent pas et qu'on relit des mois plus tard. Les deux listes sont
// donc distinctes, et le journal n'est jamais purgé par l'âge.
//
// Les entrées sont chargées par un endpoint séparé, pas avec la fiche : celle-ci
// est rechargée toutes les 15 secondes, et un journal permanent n'a pas de
// taille bornée.

import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import {
  History,
  WifiOff,
  Wifi,
  LogIn,
  LogOut,
  RotateCcw,
  Download,
  PackageCheck,
  Power,
  Terminal,
  AlertTriangle,
  ScreenShare,
  BadgeCheck,
  ArrowUpCircle,
  Wrench,
  Ban,
  FileDiff,
} from 'lucide-react';
import { api } from '../lib/api.jsx';

// L'INDEX EST LA VALEUR persistée dans `device_activities.Type` : à garder
// aligné sur l'enum `DeviceActivityType` (Models/Entities.cs). On ajoute à la
// fin, on ne renumérote jamais.
export const ACTIVITY_LABELS = [
  'Hors ligne',
  'Retour en ligne',
  'Ouverture de session',
  'Fermeture de session',
  'Redémarrage',
  'Mises à jour lancées',
  'Mises à jour terminées',
  'Redémarrage demandé',
  'Commande à distance',
  'Commande en échec',
  'Prise en main',
  'Agent enregistré',
  'Agent mis à jour',
  'Maintenance démarrée',
  'Maintenance terminée',
  'Commande annulée',
  'Système mis à jour',
  'Configuration modifiée',
];

const ACTIVITY_ICONS = [
  WifiOff,
  Wifi,
  LogIn,
  LogOut,
  RotateCcw,
  Download,
  PackageCheck,
  Power,
  Terminal,
  AlertTriangle,
  ScreenShare,
  BadgeCheck,
  ArrowUpCircle,
  Wrench,
  Wrench,
  Ban,
  ArrowUpCircle,
  FileDiff,
];

// Couleur par défaut du type. La sévérité la remplace quand elle est non nulle :
// une mise à jour terminée est bleue, la même en échec est ambre, et c'est la
// sévérité — pas le type — qui doit décider.
const ACTIVITY_TONES = [
  'text-warn',      // hors ligne
  'text-ok',        // retour en ligne
  'text-blue-400',  // ouverture de session
  'text-muted-2',   // fermeture de session
  'text-muted',     // redémarrage constaté
  'text-blue-400',  // mises à jour lancées
  'text-ok',        // mises à jour terminées
  'text-blue-400',  // redémarrage demandé
  'text-blue-400',  // commande
  'text-warn',      // commande en échec
  'text-violet-400',// prise en main
  'text-muted',     // agent enregistré
  'text-muted',     // agent mis à jour
  'text-blue-400',  // maintenance démarrée
  'text-ok',        // maintenance terminée
  'text-muted',     // commande annulée
  'text-ok',        // système mis à jour (RouterOS)
  'text-blue-400',  // configuration modifiée
];

const PAGE = 25;

const toneOf = (item) => {
  if (item.severity >= 2) return 'text-crit';
  if (item.severity === 1) return 'text-warn';
  return ACTIVITY_TONES[item.type] || 'text-muted';
};

// Les entrées sont regroupées par jour : sur une liste longue, répéter la date
// sur chaque ligne mange la largeur dont le libellé a besoin, alors que seule
// l'heure change d'une ligne à la suivante.
const dayKeyOf = (d) => {
  const date = new Date(d);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

const dayLabelOf = (d) => {
  const date = new Date(d);
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86400000);
  if (dayKeyOf(date) === dayKeyOf(today)) return "Aujourd'hui";
  if (dayKeyOf(date) === dayKeyOf(yesterday)) return 'Hier';
  return date.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: date.getFullYear() === today.getFullYear() ? undefined : 'numeric',
  });
};

const timeOf = (d) =>
  new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

export function DeviceActivityPanel({ agentId, sectionCls, sTitleCls }) {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');

  // Le rafraîchissement périodique doit lire le haut de liste courant sans
  // relancer l'intervalle à chaque nouvelle entrée : une ref plutôt que la
  // dépendance de l'effet.
  const newestId = useRef(0);
  newestId.current = items.length > 0 ? items[0].id : 0;

  const query = useCallback(
    (params) => {
      const qs = new URLSearchParams({ limit: String(PAGE) });
      if (typeFilter !== '') qs.set('type', typeFilter);
      if (params?.before) qs.set('before', String(params.before));
      return api.get(`/devices/${agentId}/activities?${qs}`);
    },
    [agentId, typeFilter]
  );

  // Chargement initial, et rechargement complet à chaque changement de filtre :
  // la pagination déjà parcourue ne vaut plus rien sur un autre sous-ensemble.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    query().then((r) => {
      if (cancelled) return;
      setItems(r?.items || []);
      setCursor(r?.nextCursor ?? null);
      setHasMore(!!r?.hasMore);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [query]);

  // Rafraîchissement : on ne recharge que la première page et on n'AJOUTE que ce
  // qui est plus récent que le haut de liste. Remplacer la liste ferait perdre
  // les pages déjà chargées à quiconque lit l'historique pendant qu'une mise à
  // jour se déroule.
  useEffect(() => {
    const iv = setInterval(async () => {
      const r = await query();
      const fresh = (r?.items || []).filter((i) => i.id > newestId.current);
      if (fresh.length > 0) setItems((prev) => [...fresh, ...prev]);
    }, 30000);
    return () => clearInterval(iv);
  }, [query]);

  const loadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    const r = await query({ before: cursor });
    setLoadingMore(false);
    if (!r) return;
    setItems((prev) => [...prev, ...(r.items || [])]);
    setCursor(r.nextCursor ?? null);
    setHasMore(!!r.hasMore);
  };

  let lastDay = null;

  return (
    <div className={sectionCls}>
      <div className={`${sTitleCls} justify-between`}>
        <span className="inline-flex items-center gap-2">
          <History size={16} strokeWidth={1.75} /> Journal d'activité
        </span>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="bg-sidebar border border-rule rounded-lg px-2 py-1 text-muted text-[11px] outline-none font-normal"
        >
          <option value="">Tous les événements</option>
          {ACTIVITY_LABELS.map((label, i) => (
            <option key={i} value={i}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="text-muted-2 text-xs py-3">Chargement...</div>
      ) : items.length === 0 ? (
        <div className="text-muted-2 text-xs py-3">
          {typeFilter === ''
            ? "Aucune activité enregistrée pour cet appareil."
            : 'Aucune activité de ce type.'}
        </div>
      ) : (
        <div className="max-h-[460px] overflow-y-auto -mx-1 px-1">
          {/* Fragment et non un <div> par entrée : l'en-tête de jour et la ligne
              doivent rester frères dans le conteneur, sinon chaque entrée serait
              à la fois la première et la dernière de son propre wrapper — les
              variantes `first:` / `last:` de Tailwind s'appliqueraient donc à
              TOUTES les lignes. Les bornes sont calculées sur l'index. */}
          {items.map((it, idx) => {
            const Icon = ACTIVITY_ICONS[it.type] || History;
            const day = dayKeyOf(it.occurredAt);
            const showDay = day !== lastDay;
            lastDay = day;
            return (
              <Fragment key={it.id}>
                {showDay && (
                  <div
                    className={`text-[10px] uppercase tracking-wide text-muted-3 font-semibold pb-1.5 ${
                      idx === 0 ? '' : 'pt-3'
                    }`}
                  >
                    {dayLabelOf(it.occurredAt)}
                  </div>
                )}
                <div
                  className={`flex items-start gap-2.5 py-2 ${
                    idx < items.length - 1 ? 'border-b border-sidebar' : ''
                  }`}
                >
                  <Icon
                    size={14}
                    strokeWidth={1.75}
                    className={`${toneOf(it)} mt-0.5 flex-shrink-0`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs font-semibold text-fg-2">{it.title}</span>
                      <span
                        className="text-[10px] text-muted-3 ml-auto flex-shrink-0"
                        title={new Date(it.occurredAt).toLocaleString('fr-FR')}
                      >
                        {timeOf(it.occurredAt)}
                      </span>
                    </div>
                    {it.message && (
                      <div className="text-[11px] text-muted-2 mt-0.5 break-words">{it.message}</div>
                    )}
                    {it.actor && (
                      <div className="text-[10px] text-muted-3 mt-0.5">par {it.actor}</div>
                    )}
                  </div>
                </div>
              </Fragment>
            );
          })}

          {hasMore && (
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className="w-full mt-2.5 bg-rule border-none rounded-lg py-2 text-muted text-[11px] font-semibold cursor-pointer hover:bg-rule-2 disabled:opacity-60"
            >
              {loadingMore ? 'Chargement...' : 'Charger plus'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
